import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { showSuccessApplyMessage } from '@framework/form/form/relations/showRelationApplyMessage.ts';
import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import {
  CONFIGURATION_DRAWER_BODY_STYLE,
  ConfigurationDrawerContent,
} from '@framework/ui/components/configuration-card/ConfigurationDrawerContent.tsx';
import { ConfigurationCardMenuSlot } from '@framework/ui/components/configuration-card/dense/ConfigurationCardMenuSlot.tsx';
import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { Button, type ButtonProps, message } from 'antd';
import produce from 'immer';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { TreeNavigatorLogic } from '../../../../../../tree/navigation/TreeNavigatorLogic.ts';
import {
  TreeReferencesContextProvider,
  useTreeReferencesContext,
} from '../../../../../../tree/references/TreeReferencesContext.tsx';
import { DrawerEditorButtonRow } from '../../../../../../ui/drawer/DrawerEditorButtonRow.tsx';
import { useSectionType } from '../../../../../sections/SectionTypeContext.tsx';
import { WidgetSelect } from '../../../../select/WidgetSelect.tsx';
import { WidgetSelectFilters } from '../../../../select/WidgetSelectFilters.tsx';
import { useConfigValidationContext } from '../../../../validation/hooks/useConfigValidationContext.ts';
import { type WidgetType } from '../../../../Widget.ts';
import { WidgetConfigForm } from '../../../common/WidgetConfigForm.tsx';
import { type IRootWidget } from '../../../containers/root/IRootWidget.ts';
import { useWidgetCreationDefaults } from '../../../WidgetCreationDefaultsContext.tsx';
import { WidgetRepository } from '../../../WidgetRepository.ts';
import {
  type ITableColumnWidget,
  type ITableInputWidget,
} from '../ITableInputWidget.ts';
import { createTableWidgetInitialColumn } from './createTableWidgetInitialColumn.ts';

const defaultValidationSchema = yup
  .object()
  .noUnknown() as yup.ObjectSchema<yup.AnyObject>;

export interface ITableColumnWidgetEditDrawerProps {
  editor: RelationEditor<ITableColumnWidget>;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
}

export function TableColumnWidgetEditDrawer(
  props: ITableColumnWidgetEditDrawerProps,
) {
  const { editor, buttonProps } = props;

  const { sectionType } = useSectionType();
  const { defaultRequired } = useWidgetCreationDefaults();
  const [selectedType, setSelectedType] = useState<WidgetType | null>(null);

  const widgetType = WidgetRepository.find(selectedType);

  // Quando viene aperto il drawer in modifica impostiamo il tipo di widget selezionato
  // per caricare correttamente il form di configurazione.
  const currentWidgetType = WidgetRepository.find(editor.handle.current?.type);

  const validator = useMemo(
    () =>
      WidgetRepository.getConfigValidationSchema(editor.handle.current) ??
      defaultValidationSchema,
    [editor.handle],
  );

  const validationContext = useConfigValidationContext({ isDraft: true });

  const { root } = useTreeReferencesContext();
  const form = useFormContext<ITableInputWidget>();
  const [tableRoot, setTableRoot] = useState(root);

  // Con questo useEffect aggiorno l'albero delle reference ogni volta che viene aggiunto una nuova colonna
  // così da gestire la generazione automatica delle reference.
  useEffect(() => {
    const tableWidget = form.getValues();

    setTableRoot(
      produce(root, (draft: IRootWidget) => {
        if (!draft) return;

        // Recupero la tabella dall'albero
        const table = TreeNavigatorLogic.byId(
          draft,
          tableWidget.id,
        ) as ITableInputWidget | null;

        if (!table) return;

        // Aggiorno la tabella coi dati del form (e.g. modifiche non salvate alle righe per esempio)
        Object.assign(table, tableWidget);

        if (!editor.handle.isNew) return;

        // Creo una colonna fittizia per il widget che sto aggiungendo
        // In questo modo sarà possibile calcolare le reference a partire da lui stesso.
        // Altrimenti non essendo presente non avrei un "punto di partenza" da cui iniziare a calcolare le reference.
        table.columns = [
          ...editor.items,
          ...(editor.handle.current ? [editor.handle.current] : []),
        ];
      }),
    );
  }, [editor.handle, editor.items, form, root]);

  return (
    <>
      <WidgetSelect
        width="60%"
        filter={WidgetSelectFilters.TableColumn}
        onChange={value => {
          setSelectedType(value);
        }}
      />
      <Button
        {...buttonProps}
        disabled={!widgetType}
        onClick={() => {
          if (!widgetType) return;

          const widget = createTableWidgetInitialColumn(
            widgetType.type,
            {
              label: 'Nuova colonna',
              code: `colonna_${editor.items.length}`,
            },
            sectionType,
            defaultRequired,
          );

          if (!widget) return;

          editor.handle.open(widget);
        }}
      >
        + Aggiungi Colonna
      </Button>
      <Drawer
        maskClosable={false}
        title="Configurazione Colonna"
        size="large"
        placement="right"
        onClose={editor.handle.cancel}
        open={editor.handle.isVisible}
        destroyOnClose
        styles={{ body: CONFIGURATION_DRAWER_BODY_STYLE }}
        // Slot per il menu delle sezioni, riempito via Portal dal group.
        extra={<ConfigurationCardMenuSlot />}
      >
        <TreeReferencesContextProvider
          root={tableRoot}
          widget={editor.handle.current!}
        >
          <FormHandler
            layout="vertical"
            schema={validator}
            initialValues={
              editor.handle.current ?? validator.cast({}, { assert: false })
            }
            validationContext={validationContext}
            onSubmit={async rawValues => {
              try {
                const values = (await validator.validate(rawValues, {
                  context: validationContext,
                })) as unknown as ITableColumnWidget;

                const result = editor.handle.apply({
                  ...editor.handle.current,
                  ...values,
                });
                showSuccessApplyMessage(result, {
                  onAdd: 'Colonna aggiunta con successo.',
                  onUpdate: 'Colonna modificata con successo.',
                });
              } catch (e) {
                // biome-ignore format: <tbd>
                message.error('Si è verificato un errore nella gestione della colonna');
                console.error(e);
              }
            }}
          >
            <ConfigurationDrawerContent>
              <WidgetConfigForm
                widget={editor.handle.current}
                widgetType={currentWidgetType}
                isTableColumn
              />
              <DrawerEditorButtonRow editor={editor} />
            </ConfigurationDrawerContent>
          </FormHandler>
        </TreeReferencesContextProvider>
      </Drawer>
    </>
  );
}
